'use client'

import type { DefaultCellComponentProps } from 'payload'
import { IconRefresh } from '@tabler/icons-react'
import { toast } from '@payloadcms/ui'
import { useRouter } from 'next/navigation'
import type { SkillTreeNode } from '@/payload-types'

export function NotionRefreshCell({ cellData, rowData }: DefaultCellComponentProps) {
  const router = useRouter()

  const node = rowData as SkillTreeNode & { id?: number }
  const nodeId = node.id
  const notionLink = node.link
  const lastUpdated = node.notionContentFetchedAt
  const treeId = typeof node.tree === 'object' ? node.tree?.id : node.tree

  // Only show refresh button for nodes with Notion links
  const hasNotionLink =
    notionLink && typeof notionLink === 'string' && notionLink.includes('notion.so')

  const handleRefresh = async () => {
    if (!nodeId || !notionLink || !treeId) {
      toast.error('Missing required information to refresh content')
      return
    }

    // Queue a background job to refresh the content
    const refreshPromise = fetch(`/api/skill-trees/${treeId}/nodes/${nodeId}/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    }).then(async (response) => {
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }))
        throw new Error(errorData.error || `HTTP ${response.status}`)
      }
      return response.json()
    })

    toast.promise(refreshPromise, {
      loading: 'Refreshing Notion content...',
      success: () => {
        // Refresh the page to show updated timestamp
        router.refresh()
        return 'Notion content refreshed successfully'
      },
      error: (error) => {
        console.error('Error refreshing Notion content:', error)
        return `Failed to refresh content: ${error instanceof Error ? error.message : 'Unknown error'}`
      },
    })
  }

  // Format the last updated timestamp
  const formatLastUpdated = (dateString: string | Date | null | undefined): string => {
    if (!dateString) return 'Never'

    try {
      const date = typeof dateString === 'string' ? new Date(dateString) : dateString
      const now = new Date()
      const diffMs = now.getTime() - date.getTime()
      const diffMins = Math.floor(diffMs / 60000)
      const diffHours = Math.floor(diffMs / 3600000)
      const diffDays = Math.floor(diffMs / 86400000)

      if (diffMins < 1) return 'Just now'
      if (diffMins < 60) return `${diffMins}m ago`
      if (diffHours < 24) return `${diffHours}h ago`
      if (diffDays < 7) return `${diffDays}d ago`

      // For older dates, show formatted date
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    } catch {
      return 'Unknown'
    }
  }

  if (!hasNotionLink) {
    return <span style={{ color: 'var(--theme-elevation-400)' }}>—</span>
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <button
        type="button"
        onClick={handleRefresh}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          padding: '4px 8px',
          fontSize: '12px',
          fontWeight: '500',
          color: '#1e40af',
          backgroundColor: '#dbeafe',
          border: '1px solid #93c5fd',
          borderRadius: '4px',
          cursor: 'pointer',
          gap: '4px',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#bfdbfe'
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = '#dbeafe'
        }}
        title="Refresh Notion content"
      >
        <IconRefresh size={14} />
        Refresh
      </button>
      {lastUpdated && (
        <span
          style={{
            fontSize: '12px',
            color: 'var(--theme-elevation-600)',
          }}
          title={
            typeof lastUpdated === 'string'
              ? new Date(lastUpdated).toLocaleString()
              : (lastUpdated as unknown as Date) instanceof Date
                ? (lastUpdated as Date).toLocaleString()
                : String(lastUpdated)
          }
        >
          {formatLastUpdated(lastUpdated)}
        </span>
      )}
    </div>
  )
}
